/**
 * Workspace Form Component
 * DS-Wiki Knowledge Share Module
 */

import { WIKI_POPUP_GRADIENT } from '@/modules/core/wiki/constants/popupHeader';
import { useState, useEffect } from 'react';
import dsWikiService from '../../services/dsWikiService';
import type { Workspace, WorkspaceInput } from '../../services/types';
import { useToast } from '../../../../../hooks/useToast';
import { FormModal, FormInput, ChangeHistory } from '../../../../../components/base';

interface WorkspaceFormProps {
  isOpen: boolean;
  workspace: Workspace | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  wrksp_nm: string;
  wrksp_cd: string;
  dscn_tx: string;
  icon_tx: string;
  clr_cd: string;
  sqnce_nu: number;
  is_dflt_in: boolean;
  a_in: boolean;
}

export default function WorkspaceForm({ isOpen, workspace, onClose, onSuccess }: WorkspaceFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { success } = useToast();
  const [formData, setFormData] = useState<FormData>({
    wrksp_nm: '', wrksp_cd: '', dscn_tx: '', icon_tx: '', clr_cd: '', sqnce_nu: 0, is_dflt_in: false, a_in: true,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    if (workspace) {
      setFormData({
        wrksp_nm: workspace.wrksp_nm || '',
        wrksp_cd: workspace.wrksp_cd || '',
        dscn_tx: workspace.dscn_tx || '',
        icon_tx: workspace.icon_tx || '',
        clr_cd: workspace.clr_cd || '',
        sqnce_nu: workspace.sqnce_nu || 0,
        is_dflt_in: workspace.is_dflt_in || false,
        a_in: workspace.a_in !== false,
      });
    } else {
      setFormData({ wrksp_nm: '', wrksp_cd: '', dscn_tx: '', icon_tx: '', clr_cd: '', sqnce_nu: 0, is_dflt_in: false, a_in: true });
    }
    setErrors({});
    setApiError(null);
  }, [workspace, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    if (errors[name as keyof FormData]) setErrors(prev => ({ ...prev, [name]: undefined }));
    if (apiError) setApiError(null);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!formData.wrksp_nm.trim()) newErrors.wrksp_nm = 'Workspace name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const dataToSave: WorkspaceInput = {
        wrksp_nm: formData.wrksp_nm,
        wrksp_cd: formData.wrksp_cd || undefined,
        dscn_tx: formData.dscn_tx || undefined,
        icon_tx: formData.icon_tx || undefined,
        clr_cd: formData.clr_cd || undefined,
        sqnce_nu: formData.sqnce_nu,
        is_dflt_in: formData.is_dflt_in,
        a_in: formData.a_in,
      };
      if (workspace?.dswiki_wrksp_id) dataToSave.dswiki_wrksp_id = workspace.dswiki_wrksp_id;
      await dsWikiService.saveWorkspace(dataToSave);
      success(workspace ? 'Workspace updated successfully!' : 'Workspace created successfully!');
      onSuccess();
      onClose();
    } catch (error: any) {
      setApiError(error?.message || 'Failed to save workspace');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormModal popup={{ gradient: WIKI_POPUP_GRADIENT, icon: 'ri-layout-4-line' }} isOpen={isOpen} onClose={onClose} onSubmit={handleSubmit} title={workspace ? 'Edit Workspace' : 'Add New Workspace'} submitText={workspace ? 'Update' : 'Create'} submitColor="indigo" isSubmitting={isSubmitting}>
      {apiError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0 mt-0.5"></i>
          <div className="text-sm text-red-700">{apiError}</div>
        </div>
      )}

      <FormInput label="Workspace Name" name="wrksp_nm" value={formData.wrksp_nm} onChange={handleInputChange} placeholder="e.g., General, Urban Planning" required error={errors.wrksp_nm} />
      <FormInput label="Code" name="wrksp_cd" value={formData.wrksp_cd} onChange={handleInputChange} placeholder="e.g., GEN, UP" helperText="Optional short code" />
      <FormInput label="Description" name="dscn_tx" value={formData.dscn_tx} onChange={handleInputChange} placeholder="Brief description" />

      <div className="grid grid-cols-2 gap-4">
        <FormInput label="Icon Class" name="icon_tx" value={formData.icon_tx} onChange={handleInputChange} placeholder="e.g., ri-folder-line" helperText="Remix Icon class" />
        <FormInput label="Color" name="clr_cd" type="color" value={formData.clr_cd || '#6366f1'} onChange={handleInputChange} />
      </div>

      <FormInput label="Sequence" name="sqnce_nu" type="number" value={formData.sqnce_nu.toString()} onChange={handleInputChange} placeholder="Display order" />

      <div className="grid grid-cols-2 gap-4 mt-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" name="is_dflt_in" checked={formData.is_dflt_in} onChange={handleInputChange} className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
          <span className="text-sm text-gray-700">Default Workspace</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" name="a_in" checked={formData.a_in} onChange={handleInputChange} className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
          <span className="text-sm text-gray-700">Active</span>
        </label>
      </div>

      {workspace && <ChangeHistory lastChange={(workspace as any).lastChange} />}
    </FormModal>
  );
}
